import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useExceptionAlerts } from '@/hooks/use-asset-management-widgets';
import { AlertTriangle, AlertCircle, Info, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export function ExceptionAlertsWidget() {
  const { data, isLoading, error } = useExceptionAlerts(5);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Exception Alerts</CardTitle>
              <CardDescription>Critical issues requiring attention</CardDescription>
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-950 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Handle error state (API failure)
  if (error) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Exception Alerts</CardTitle>
              <CardDescription>Critical issues requiring attention</CardDescription>
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-950 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            Unable to load exception alerts
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle empty data state (no exceptions in database)
  if (!data) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Exception Alerts</CardTitle>
              <CardDescription>Critical issues requiring attention</CardDescription>
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-950 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col h-[300px] items-center justify-center text-center">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No exception alerts
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'high':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'medium':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case 'low':
        return <Info className="h-5 w-5 text-blue-500" />;
      default:
        return <Info className="h-5 w-5 text-gray-500" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-500">High</Badge>;
      case 'medium':
        return <Badge className="bg-amber-500">Medium</Badge>;
      case 'low':
        return <Badge variant="secondary">Low</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const handleAssetClick = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col')}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Exception Alerts</CardTitle>
            <CardDescription>Critical issues requiring attention</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-2">
              <div className="text-center">
                <div className="text-lg font-bold text-red-600 dark:text-red-400">{data.criticalCount}</div>
                <div className="text-[10px] text-muted-foreground">Critical</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{data.warningCount}</div>
                <div className="text-[10px] text-muted-foreground">Warning</div>
              </div>
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-950 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {data.alerts.length === 0 ? (
          <div className="flex h-[200px] items-center justify-center text-muted-foreground">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p className="text-sm">No exception alerts</p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              {data.alerts.slice(0, 4).map((alert) => (
                <button
                  type="button"
                  key={alert.id}
                  className="rounded-md border p-2.5 hover:bg-accent cursor-pointer transition-colors group w-full text-left"
                  onClick={() => alert.assetId && handleAssetClick(alert.assetId)}
                  aria-label={`View ${alert.assetName ?? 'System Alert'} exception details`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      {getSeverityIcon(alert.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-sm truncate">{alert.assetName ?? 'System Alert'}</div>
                          {getSeverityBadge(alert.severity)}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{alert.type}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(alert.createdDate), 'MMM d')}
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {data.alerts.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate('/audits/exceptions')}
                >
                  View All {data.totalCount} Exceptions
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
